import React from "react";
import { useNavigate } from "react-router-dom";

function StepIcon({ type }) {
  const common = {
    width: 48,
    height: 48,
    viewBox: "0 0 48 48",
    fill: "none",
    stroke: "currentColor",
    strokeWidth: 2.4,
    strokeLinecap: "round",
    strokeLinejoin: "round",
    "aria-hidden": true,
  };

  switch (type) {
    case "qr":
      return (
        <svg {...common}>
          <rect x="4" y="4" width="15" height="15" rx="1" />
          <rect x="8" y="8" width="7" height="7" />
          <rect x="29" y="4" width="15" height="15" rx="1" />
          <rect x="33" y="8" width="7" height="7" />
          <rect x="4" y="29" width="15" height="15" rx="1" />
          <rect x="8" y="33" width="7" height="7" />
          <path d="M25 4v7h4M25 16h5v5M35 23h9v5h-6v6M23 29h5v5h-5v10M32 38h5v6M42 38v6" />
        </svg>
      );

    case "alert":
      return (
        <svg {...common}>
          <path d="M8 33h32l-4-5V19a12 12 0 0 0-24 0v9z" />
          <path d="M19 38a5 5 0 0 0 10 0M24 2v4M5 10l4 4M43 10l-4 4" />
          <path d="M24 13v9M24 27h.01" />
        </svg>
      );

    case "medical":
      return (
        <svg {...common}>
          <path d="M24 3 41 9v13c0 11-7 18-17 23C14 40 7 33 7 22V9z" />
          <path d="M24 14v17M15.5 22.5h17" />
        </svg>
      );

    case "assistance":
      return (
        <svg {...common}>
          <circle cx="24" cy="10" r="6" />
          <path d="M12 29c0-7 5-12 12-12s12 5 12 12" />
          <path d="M7 43v-5c0-5 3-8 8-8M41 43v-5c0-5-3-8-8-8" />
          <path d="M18 26h12M21 22h6M21 34l3 3 6-7" />
        </svg>
      );

    default:
      return null;
  }
}

function ArrowRight() {
  return (
    <span
      className="inline-block text-[19px] leading-none transition-transform duration-200 group-hover:translate-x-1"
      aria-hidden="true"
    >
      →
    </span>
  );
}

export default function ForPartners() {
  const navigate = useNavigate();

  return (
    <div className="w-full overflow-hidden bg-white text-[#344b3a]" id="home">
      {/* Hero */}
      <section className="grid min-h-[440px] grid-cols-1 bg-gradient-to-br from-[#f4faf5] to-[#e5f4e8] md:grid-cols-[1.05fr_0.95fr]">
        <div className="self-center px-[6%] py-10 md:px-[9%] md:py-11">
          <p className="mb-3 text-sm font-extrabold tracking-wide text-[#168447]">
            PARTNER WITH LIFEONPLUS
          </p>

          <h1 className="mb-4 text-[clamp(38px,4.4vw,58px)] font-extrabold leading-[1.06] tracking-tight text-[#14552b]">
            Life Saver on
            <br />
            Every Ride
          </h1>

          <h3 className="my-3 text-base font-normal leading-relaxed text-[#168447] md:text-[17px]">
            Safer roads start with smarter emergency readiness.
          </h3>

          <p className="mb-6 max-w-[510px] text-[13px] leading-[1.85] text-[#43594a] md:text-sm">
            LifeOnPlus adds a Digital Emergency Health Kit to helmets and
            vehicles. In a critical moment, a simple QR scan can surface vital
            medical details and help connect the right people faster.
          </p>

          <a
            href="#contact"
            className="group inline-flex items-center justify-center gap-2.5 rounded-full bg-[#168447] px-6 py-3.5 text-[13px] font-bold text-white transition-all duration-200 hover:-translate-y-0.5 hover:bg-[#14552b] hover:shadow-[0_7px_18px_rgba(22,132,71,0.2)]"
          >
            Become a Partner <ArrowRight />
          </a>
        </div>

        <div className="h-[260px] w-full min-w-0 overflow-hidden md:h-auto md:min-h-[440px]">
          <img
            src="helmets.jpeg"
            alt="LifeOnPlus emergency QR code on a helmet"
            className="h-full w-full object-cover object-center"
          />
        </div>
      </section>

      {/* Key Benefits */}
      <section
        className="grid grid-cols-1 gap-6 border-b border-[#dcebe0] bg-white px-[6%] py-7 md:grid-cols-3 md:gap-8 md:px-[6%] md:py-8"
        id="about"
      >
        <div className="flex min-w-0 items-center gap-4">
          <span className="flex h-[56px] w-[56px] shrink-0 items-center justify-center rounded-full bg-[#168447] text-[28px] font-bold text-white md:h-[62px] md:w-[62px]">
            ♡
          </span>
          <div>
            <h3 className="mb-1.5 text-[15px] font-bold leading-snug text-[#14552b]">
              Faster Response
            </h3>
            <p className="m-0 text-[13px] leading-relaxed text-[#536967]">
              Critical information is available in seconds when every moment
              counts.
            </p>
          </div>
        </div>

        <div className="flex min-w-0 items-center gap-4">
          <span className="flex h-[56px] w-[56px] shrink-0 items-center justify-center rounded-full bg-[#168447] text-[28px] font-bold text-white md:h-[62px] md:w-[62px]">
            +
          </span>
          <div>
            <h3 className="mb-1.5 text-[15px] font-bold leading-snug text-[#14552b]">
              Emergency Health Data
            </h3>
            <p className="m-0 text-[13px] leading-relaxed text-[#536967]">
              Blood group, allergies, conditions and emergency contacts — ready
              when needed.
            </p>
          </div>
        </div>

        <div className="flex min-w-0 items-center gap-4">
          <span className="flex h-[56px] w-[56px] shrink-0 items-center justify-center rounded-full bg-[#168447] text-[28px] font-bold text-white md:h-[62px] md:w-[62px]">
            ↗
          </span>
          <div>
            <h3 className="mb-1.5 text-[15px] font-bold leading-snug text-[#14552b]">
              Trusted Partnership
            </h3>
            <p className="m-0 text-[13px] leading-relaxed text-[#536967]">
              Join a growing safety brand and open new channels for your
              products.
            </p>
          </div>
        </div>
      </section>

      {/* Vehicle Applications */}
      <section
        className="bg-white px-[5%] py-11 md:px-[5.1%] md:py-14"
        id="vehicles"
      >
        <div className="mx-auto mb-7 text-center md:mb-8">
          <h2 className="mb-3 text-[clamp(26px,3vw,36px)] font-extrabold leading-tight text-[#14552b]">
            One Kit. Many Vehicles.
          </h2>
          <p className="mx-auto max-w-[680px] text-[13px] leading-relaxed text-[#536967] md:text-sm">
            From daily two-wheelers to commercial fleets — LifeOnPlus fits where
            safety matters most.
          </p>
        </div>

        <div className="mb-5 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 lg:gap-5">
          <article className="min-w-0 overflow-hidden rounded-[14px] border border-[#dcebe0] bg-white pb-[18px] shadow-[0_5px_18px_rgba(13,73,63,0.05)] transition-all duration-250 hover:-translate-y-1 hover:shadow-[0_12px_28px_rgba(13,73,63,0.12)]">
            <img
              src="helmets.jpeg"
              alt="Helmet with LifeOnPlus QR code"
              className="h-[140px] w-full object-cover sm:h-[155px] lg:h-[250px]"
            />
            <h3 className="mx-3.5 mt-3.5 mb-1.5 text-sm font-extrabold leading-snug text-[#14552b] sm:text-base">
              Helmets
            </h3>
            <p className="mx-3.5 text-xs leading-relaxed text-[#61736e] sm:text-[13px]">
              Protect two-wheeler riders with always-ready emergency access.
            </p>
          </article>

          <article className="min-w-0 overflow-hidden rounded-[14px] border border-[#dcebe0] bg-white pb-[18px] shadow-[0_5px_18px_rgba(13,73,63,0.05)] transition-all duration-250 hover:-translate-y-1 hover:shadow-[0_12px_28px_rgba(13,73,63,0.12)]">
            <img
              src="cars.jpeg"
              alt="Car with LifeOnPlus QR code"
              className="h-[140px] w-full object-cover sm:h-[155px] lg:h-[250px]"
            />
            <h3 className="mx-3.5 mt-3.5 mb-1.5 text-sm font-extrabold leading-snug text-[#14552b] sm:text-base">
              Cars
            </h3>
            <p className="mx-3.5 text-xs leading-relaxed text-[#61736e] sm:text-[13px]">
              Add peace of mind for personal and family four-wheelers.
            </p>
          </article>

          <article className="min-w-0 overflow-hidden rounded-[14px] border border-[#dcebe0] bg-white pb-[18px] shadow-[0_5px_18px_rgba(13,73,63,0.05)] transition-all duration-250 hover:-translate-y-1 hover:shadow-[0_12px_28px_rgba(13,73,63,0.12)]">
            <img
              src="two_wheelers.jpg"
              alt="Scooter with LifeOnPlus QR code"
              className="h-[140px] w-full object-cover sm:h-[155px] lg:h-[250px]"
            />
            <h3 className="mx-3.5 mt-3.5 mb-1.5 text-sm font-extrabold leading-snug text-[#14552b] sm:text-base">
              Scooters &amp; Bikes
            </h3>
            <p className="mx-3.5 text-xs leading-relaxed text-[#61736e] sm:text-[13px]">
              Everyday mobility deserves everyday emergency readiness.
            </p>
          </article>
        </div>

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 lg:gap-5">
          <article className="min-w-0 overflow-hidden rounded-[14px] border border-[#dcebe0] bg-white pb-[18px] shadow-[0_5px_18px_rgba(13,73,63,0.05)] transition-all duration-250 hover:-translate-y-1 hover:shadow-[0_12px_28px_rgba(13,73,63,0.12)]">
            <img
              src="keychain.jpeg"
              alt="Keychain with LifeOnPlus QR code"
              className="h-[140px] w-full object-cover sm:h-[155px] lg:h-[250px]"
            />
            <h3 className="mx-3.5 mt-3.5 mb-1.5 text-sm font-extrabold leading-snug text-[#14552b] sm:text-base">
              Keychain
            </h3>
            <p className="mx-3.5 text-xs leading-relaxed text-[#61736e] sm:text-[13px]">
              Carry emergency access on your keys every day.
            </p>
          </article>

          <article className="min-w-0 overflow-hidden rounded-[14px] border border-[#dcebe0] bg-white pb-[18px] shadow-[0_5px_18px_rgba(13,73,63,0.05)] transition-all duration-250 hover:-translate-y-1 hover:shadow-[0_12px_28px_rgba(13,73,63,0.12)]">
            <img
              src="bottle.jpeg"
              alt="Water bottle with LifeOnPlus QR code"
              className="h-[140px] w-full object-cover sm:h-[155px] lg:h-[250px]"
            />
            <h3 className="mx-3.5 mt-3.5 mb-1.5 text-sm font-extrabold leading-snug text-[#14552b] sm:text-base">
              Water Bottles
            </h3>
            <p className="mx-3.5 text-xs leading-relaxed text-[#61736e] sm:text-[13px]">
              Stay hydrated and stay protected with a smart bottle.
            </p>
          </article>

          <article className="min-w-0 overflow-hidden rounded-[14px] border border-[#dcebe0] bg-white pb-[18px] shadow-[0_5px_18px_rgba(13,73,63,0.05)] transition-all duration-250 hover:-translate-y-1 hover:shadow-[0_12px_28px_rgba(13,73,63,0.12)]">
            <img
              src="purse.jpeg"
              alt="Purse with LifeOnPlus QR code"
              className="h-[140px] w-full object-cover sm:h-[155px] lg:h-[250px]"
            />
            <h3 className="mx-3.5 mt-3.5 mb-1.5 text-sm font-extrabold leading-snug text-[#14552b] sm:text-base">
              Purse
            </h3>
            <p className="mx-3.5 text-xs leading-relaxed text-[#61736e] sm:text-[13px]">
              Keep vital emergency details close at hand.
            </p>
          </article>
        </div>
      </section>

      {/* How It Works */}
      <section
        className="w-full overflow-visible bg-gradient-to-br from-[#edf7ef] to-[#f9fcf9] px-[5%] py-11 md:px-[5.3%] md:py-[70px]"
        id="how"
      >
        <div className="mx-auto mb-7 w-full text-center md:mb-9">
          <h2 className="mb-3 text-[clamp(29px,3vw,38px)] font-extrabold leading-tight text-[#14552b]">
            How It Works
          </h2>
          <p className="mx-auto max-w-[680px] text-[13px] leading-relaxed text-[#536967] md:text-sm">
            Four simple steps from scan to support.
          </p>
        </div>

        <div className="mx-auto grid w-full max-w-[1250px] grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4 lg:gap-[22px]">
          {/* Step 1 */}
          <article className="flex min-w-0 flex-col overflow-visible rounded-[18px] border border-[#dcebe0] bg-white p-3.5 shadow-[0_8px_25px_rgba(6,73,74,0.07)] transition-all duration-250 hover:-translate-y-1.5 hover:border-[#9ed3ad] hover:shadow-[0_16px_34px_rgba(6,73,74,0.13)] sm:p-5 lg:p-[18px]">
            <img
              src="scan_qr_in_emergency.jpg"
              alt="Scanning a QR code on a helmet"
              className="mb-3.5 h-[140px] w-full rounded-xl object-cover object-center sm:mb-[18px] sm:h-[180px] lg:h-[180px]"
            />
            <div className="mb-3.5 flex min-h-[42px] items-center justify-between gap-2 sm:mb-[18px] sm:min-h-[58px] sm:gap-3">
              <span className="inline-flex h-[34px] w-[34px] shrink-0 items-center justify-center rounded-full border border-[#b9ddc2] bg-[#e5f4e8] text-sm font-extrabold text-[#168447] sm:h-[42px] sm:w-[42px] sm:text-[17px]">
                1
              </span>
              <span className="inline-flex h-[42px] w-[42px] shrink-0 items-center justify-center overflow-visible rounded-[12px] bg-[#edf7ef] text-[#168447] sm:h-[56px] sm:w-[56px] sm:rounded-[15px]">
                <StepIcon type="qr" />
              </span>
            </div>
            <h3 className="mb-2 text-sm font-extrabold leading-snug text-[#14552b] sm:mb-2.5 sm:text-lg">
              Scan the QR
            </h3>
            <p className="m-0 text-xs leading-relaxed text-[#596f68] sm:text-sm">
              Anyone nearby can scan the code on the helmet or vehicle with a
              regular smartphone.
            </p>
          </article>

          {/* Step 2 */}
          <article className="flex min-w-0 flex-col overflow-visible rounded-[18px] border border-[#dcebe0] bg-white p-3.5 shadow-[0_8px_25px_rgba(6,73,74,0.07)] transition-all duration-250 hover:-translate-y-1.5 hover:border-[#9ed3ad] hover:shadow-[0_16px_34px_rgba(6,73,74,0.13)] sm:p-5 lg:p-[18px]">
            <img
              src="instant_emergency_alert.jpg"
              alt="Emergency alert on a mobile phone"
              className="mb-3.5 h-[140px] w-full rounded-xl object-cover object-center sm:mb-[18px] sm:h-[180px] lg:h-[180px]"
            />
            <div className="mb-3.5 flex min-h-[42px] items-center justify-between gap-2 sm:mb-[18px] sm:min-h-[58px] sm:gap-3">
              <span className="inline-flex h-[34px] w-[34px] shrink-0 items-center justify-center rounded-full border border-[#b9ddc2] bg-[#e5f4e8] text-sm font-extrabold text-[#168447] sm:h-[42px] sm:w-[42px] sm:text-[17px]">
                2
              </span>
              <span className="inline-flex h-[42px] w-[42px] shrink-0 items-center justify-center overflow-visible rounded-[12px] bg-[#edf7ef] text-[#168447] sm:h-[56px] sm:w-[56px] sm:rounded-[15px]">
                <StepIcon type="alert" />
              </span>
            </div>
            <h3 className="mb-2 text-sm font-extrabold leading-snug text-[#14552b] sm:mb-2.5 sm:text-lg">
              Trigger Alert
            </h3>
            <p className="m-0 text-xs leading-relaxed text-[#596f68] sm:text-sm">
              Configured emergency contacts can be notified so help starts
              moving sooner.
            </p>
          </article>

          {/* Step 3 */}
          <article className="flex min-w-0 flex-col overflow-visible rounded-[18px] border border-[#dcebe0] bg-white p-3.5 shadow-[0_8px_25px_rgba(6,73,74,0.07)] transition-all duration-250 hover:-translate-y-1.5 hover:border-[#9ed3ad] hover:shadow-[0_16px_34px_rgba(6,73,74,0.13)] sm:p-5 lg:p-[18px]">
            <img
              src="access_medical_information.jpg"
              alt="Emergency medical information on a phone"
              className="mb-3.5 h-[140px] w-full rounded-xl object-cover object-center sm:mb-[18px] sm:h-[180px] lg:h-[180px]"
            />
            <div className="mb-3.5 flex min-h-[42px] items-center justify-between gap-2 sm:mb-[18px] sm:min-h-[58px] sm:gap-3">
              <span className="inline-flex h-[34px] w-[34px] shrink-0 items-center justify-center rounded-full border border-[#b9ddc2] bg-[#e5f4e8] text-sm font-extrabold text-[#168447] sm:h-[42px] sm:w-[42px] sm:text-[17px]">
                3
              </span>
              <span className="inline-flex h-[42px] w-[42px] shrink-0 items-center justify-center overflow-visible rounded-[12px] bg-[#edf7ef] text-[#168447] sm:h-[56px] sm:w-[56px] sm:rounded-[15px]">
                <StepIcon type="medical" />
              </span>
            </div>
            <h3 className="mb-2 text-sm font-extrabold leading-snug text-[#14552b] sm:mb-2.5 sm:text-lg">
              View Health Info
            </h3>
            <p className="m-0 text-xs leading-relaxed text-[#596f68] sm:text-sm">
              Access blood group, allergies, medical notes and other details
              based on privacy settings.
            </p>
          </article>

          {/* Step 4 */}
          <article className="flex min-w-0 flex-col overflow-visible rounded-[18px] border border-[#dcebe0] bg-white p-3.5 shadow-[0_8px_25px_rgba(6,73,74,0.07)] transition-all duration-250 hover:-translate-y-1.5 hover:border-[#9ed3ad] hover:shadow-[0_16px_34px_rgba(6,73,74,0.13)] sm:p-5 lg:p-[18px]">
            <img
              src="timely_assistance.jpg"
              alt="Emergency responders providing assistance"
              className="mb-3.5 h-[140px] w-full rounded-xl object-cover object-center sm:mb-[18px] sm:h-[180px] lg:h-[180px]"
            />
            <div className="mb-3.5 flex min-h-[42px] items-center justify-between gap-2 sm:mb-[18px] sm:min-h-[58px] sm:gap-3">
              <span className="inline-flex h-[34px] w-[34px] shrink-0 items-center justify-center rounded-full border border-[#b9ddc2] bg-[#e5f4e8] text-sm font-extrabold text-[#168447] sm:h-[42px] sm:w-[42px] sm:text-[17px]">
                4
              </span>
              <span className="inline-flex h-[42px] w-[42px] shrink-0 items-center justify-center overflow-visible rounded-[12px] bg-[#edf7ef] text-[#168447] sm:h-[56px] sm:w-[56px] sm:rounded-[15px]">
                <StepIcon type="assistance" />
              </span>
            </div>
            <h3 className="mb-2 text-sm font-extrabold leading-snug text-[#14552b] sm:mb-2.5 sm:text-lg">
              Get Help Faster
            </h3>
            <p className="m-0 text-xs leading-relaxed text-[#596f68] sm:text-sm">
              Responders and contacts act with clearer information when every
              second matters.
            </p>
          </article>
        </div>
      </section>

      {/* Why Partner */}
      <section
        className="bg-white px-[5%] py-11 md:px-[5.1%] md:py-[60px]"
        id="partners"
      >
        <div className="mx-auto mb-7 text-center md:mb-8">
          <h2 className="mb-3 text-[clamp(26px,3vw,36px)] font-extrabold leading-tight text-[#14552b]">
            Why Partner with LifeOnPlus?
          </h2>
          <p className="mx-auto max-w-[680px] text-[13px] leading-relaxed text-[#536967] md:text-sm">
            Build a safer product story and open new commercial opportunities at
            the same time.
          </p>
        </div>

        <div className="grid grid-cols-1 gap-5 md:grid-cols-3 md:gap-6">
          <article className="min-w-0 overflow-hidden rounded-[15px] border border-[#dcebe0] bg-white pb-[22px] shadow-[0_6px_22px_rgba(13,73,63,0.06)] transition-all duration-250 hover:-translate-y-1 hover:shadow-[0_13px_30px_rgba(13,73,63,0.12)]">
            <img
              src="oem_integration.jpg"
              alt="Vehicle manufacturing and helmet integration"
              className="h-[190px] w-full object-cover md:h-[190px]"
            />
            <h3 className="mx-[15px] mt-[15px] mb-2 text-base font-extrabold leading-snug text-[#14552b] md:mx-[18px] md:mt-[18px] md:text-[17px]">
              OEM Integration
            </h3>
            <p className="mx-[15px] text-[13px] leading-relaxed text-[#61716d] md:mx-[18px]">
              Embed the Digital Emergency Health Kit into helmets and vehicles.
              Co-branded options help you stand out while adding real safety
              value.
            </p>
          </article>

          <article className="min-w-0 overflow-hidden rounded-[15px] border border-[#dcebe0] bg-white pb-[22px] shadow-[0_6px_22px_rgba(13,73,63,0.06)] transition-all duration-250 hover:-translate-y-1 hover:shadow-[0_13px_30px_rgba(13,73,63,0.12)]">
            <img
              src="build_a_safer_world_together.jpg"
              alt="Business partners shaking hands"
              className="h-[190px] w-full object-cover md:h-[190px]"
            />
            <h3 className="mx-[15px] mt-[15px] mb-2 text-base font-extrabold leading-snug text-[#14552b] md:mx-[18px] md:mt-[18px] md:text-[17px]">
              Bulk &amp; White Label
            </h3>
            <p className="mx-[15px] text-[13px] leading-relaxed text-[#61716d] md:mx-[18px]">
              Scale with volume pricing, custom packaging and white-label
              solutions tailored to your brand and distribution network.
            </p>
          </article>

          <article className="min-w-0 overflow-hidden rounded-[15px] border border-[#dcebe0] bg-white pb-[22px] shadow-[0_6px_22px_rgba(13,73,63,0.06)] transition-all duration-250 hover:-translate-y-1 hover:shadow-[0_13px_30px_rgba(13,73,63,0.12)]">
            <img
              src="road_safety_initiative.jpg"
              alt="Road safety initiative"
              className="h-[190px] w-full object-cover md:h-[190px]"
            />
            <h3 className="mx-[15px] mt-[15px] mb-2 text-base font-extrabold leading-snug text-[#14552b] md:mx-[18px] md:mt-[18px] md:text-[17px]">
              CSR &amp; Road Safety
            </h3>
            <p className="mx-[15px] text-[13px] leading-relaxed text-[#61716d] md:mx-[18px]">
              Partner on awareness campaigns and safety programs that improve
              emergency preparedness and public trust.
            </p>
          </article>
        </div>
      </section>

      {/* Partnership CTA */}
      <section
        className="mx-[4%] mb-[18px] grid grid-cols-1 items-center gap-[18px] rounded-[14px] border border-[#dcebe0] bg-gradient-to-br from-[#edf7ef] to-[#f8fcf8] px-[5%] py-[26px] md:mx-[3.6%] md:mb-6 md:grid-cols-[1.5fr_1fr] md:gap-7 md:rounded-[18px] md:px-[4.5%] md:py-9"
        id="contact"
      >
        <div className="flex min-w-0 items-start gap-3 md:items-center md:gap-5">
          <span className="flex h-10 w-10 shrink-0 items-center justify-center text-[32px] leading-none text-[#168447] md:h-[58px] md:w-[58px] md:text-[42px]">
            ♡
          </span>
          <div>
            <h2 className="mb-2 text-xl font-extrabold leading-snug text-[#14552b] md:text-[clamp(21px,2vw,28px)]">
              Let’s Make Every Ride Safer
            </h2>
            <p className="m-0 max-w-[460px] text-[13px] leading-relaxed text-[#526d66] md:text-sm">
              Partner with LifeOnPlus to bring emergency readiness to more
              helmets, vehicles and roads.
            </p>
          </div>
        </div>

        <div className="flex min-w-0 flex-col items-start gap-2.5 border-t border-[#b9d9c1] pt-[18px] md:border-t-0 md:border-l md:pl-7 md:pt-0">
          <div
            className="group inline-flex cursor-pointer items-center justify-center gap-2.5 rounded-full bg-[#168447] px-[22px] py-3.5 text-[13px] font-bold text-white transition-all duration-200 hover:-translate-y-0.5 hover:bg-[#14552b] hover:shadow-[0_7px_18px_rgba(22,132,71,0.2)]"
            onClick={() => navigate("/contact")}
          >
            Enquire About Partnership <ArrowRight />
          </div>
          <p className="m-0 text-xs leading-normal text-[#5a7368]">
            We typically respond within 1–2 business days.
          </p>
        </div>
      </section>
    </div>
  );
}
